<template>
  <div :class="{
    'map-sidebar': true,
    'map-sidebar--visible': isVisible,
  }">
    <div class="map-sidebar__header">
      <a class="map-sidebar__back" @click="toggle">
        <ArrowLeft />
      </a>
      <h3 class="map-sidebar__headline">eFriends in der Umgebung</h3>
    </div>
    <div class="map-sidebar__actions">
      <CubeFilter
        :num-results="numResults"
        class="map-sidebar__filter"
        expandedByDefault
        @filter-changed="onCubeFilterChanged"
      />
      <SortButton />
    </div>
    <Spinner v-if="isLoading" class="map-sidebar__spinner" />
    <ul v-else class="map-sidebar__list" data-cy="sidebar-list">
      <li v-for="cube in visibleCubes" :key="cube.params.cubeName">
        <SidebarEntry :cube="cube" />
      </li>
    </ul>
    <button
      class="button button--secondary button--full-width map-sidebar__more-button"
      v-if="cubeDisplayLimit < totalNumCubes"
      @click.prevent="showMoreCubes()"
    >
      {{ t('efriends.showMore') }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import ArrowLeft from '@/components/icons/ArrowLeft.vue'
  import CubeFilter from '@/components/efriends/CubeFilter.vue'
  import SortButton from '@/components/efriends/SortButton.vue'
  import SidebarEntry from '@/components/efriends/SidebarEntry.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed, ref, toRef } from 'vue'
  import { useRouter } from 'vue-router'
  import { sortFactory } from '@/components/efriends/sortFactory.ts'
  import Spinner from '@/components/icons/Spinner.vue'
  import { useMapSidebar } from '@/composables/useMapSidebar.ts'
  import { useMapStore } from '@/stores/useMapStore'
import type { CubeMapData } from '@/types/CubeDto'

  const mapStore = useMapStore()
  const { t } = useStrings()
  const router = useRouter()
  const { show, hide, toggle, isVisible } = useMapSidebar()

  const props = defineProps({
    isLoading: {
      type: Boolean,
      default: true,
    },
    filteredCubes: {
      required: true,
      type: Array<CubeMapData>,
    },
    numResults: {
      required: true,
      type: Number,
    },
  })

  const emit = defineEmits(['filter-changed'])

  const onCubeFilterChanged = () => {
    emit('filter-changed')
  }

  const filteredCubes = toRef(() => props.filteredCubes)

  const cubeDisplayLimit = ref(100)
  const cubeDisplayStep = ref(150)
  const visibleCubes = computed(() => {
    return filteredCubes.value
      .sort(sortFactory(mapStore.cubeOrder, mapStore))
      .slice(0, cubeDisplayLimit.value)
  })

  const totalNumCubes = computed(() => filteredCubes.value.length)

  const showMoreCubes = () => {
    cubeDisplayLimit.value += cubeDisplayStep.value
  }
</script>

<style lang="scss" scoped>
  .map-sidebar {
    width: $map-sidebar-width-min;
    position: fixed;
    top: 0;
    right: 0;
    bottom: $nav-height;
    background: $white;
    padding: $spacer20 $spacer10;
    overflow-y: auto;
    transition: $transition-base;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);

    @media (max-width: ($bp-lg - 1px)) {
      transform: translateX(100%);

      &--visible {
        transform: translateX(0%);
      }
    }

    @media (min-width: $bp-sm) {
      position: absolute;
      bottom: 0;
      width: $map-sidebar-width-sm;
    }

    @media (min-width: $bp-lg) {
      transform: translateX(0%);
      width: $map-sidebar-width-lg;
    }

    &__header {
      margin-bottom: $spacer10;
      display: flex;
      align-items: center;
    }

    &__back {
      width: 1rem;
      margin-right: $spacer10;
      transition: $transition-base;
      color: $grey-mediumdark;
      display: flex;
      align-items: center;
      transform: rotate(180deg);

      @media (min-width: $bp-lg) {
        display: none;
      }

      &:hover,
      &:focus,
      &:active {
        color: $black;
      }
    }

    &__headline {
      margin: 0;
    }

    &__spinner {
      animation: spin-centered 1.8s linear 0s infinite;
      width: $spacer20;
      display: block;
      margin: $spacer10 auto;
    }

    &__actions {
      margin-bottom: $spacer30;

      > * + * {
        margin-top: $spacer05;
      }
    }

    &__more-button {
      margin-bottom: $spacer20;
    }

    &__list {
      list-style: none;
      margin: 0 0 $spacer20 0;
      padding: 0;

      > * + * {
        margin-top: $spacer10;
      }
    }
  }
</style>
